// Task titles can hold formulas that become a number from the next stay, for example
// "Put out /days*guests*2 towels" or "Change /guests*(1every3days) sheets". The terms are days, guests,
// any number, and (NeveryMdays): N for each started period of M days. They are multiplied together
// and a result with decimals is rounded up. The title is stored as written and worked out when shown,
// so it follows changes to the booking.

const NUMBER = '\\d+(?:[.,]\\d+)?';
const EVERY = `\\(\\s*(${NUMBER})\\s*every\\s*(\\d+)\\s*days?\\s*\\)`;
const TERM = `(?:days|guests|${NUMBER}|\\(\\s*${NUMBER}\\s*every\\s*\\d+\\s*days?\\s*\\))`;
const FORMULA = new RegExp(`(^|[^\\w/])/(${TERM}(?:\\s*\\*\\s*${TERM})*)(?![\\w*])`, 'gi');
const EVERY_TERM = new RegExp(`^${EVERY}$`, 'i');

// Without a stay in the next week, this is what the numbers are worked out from.
const ASSUMED_STAY = { days: 3, guests: 5 };

const parseNumber = (text) => Number(text.replace(',', '.'));

const termValue = (term, stay) => {
  const lower = term.toLowerCase();
  if (lower === 'days') return stay.days;
  if (lower === 'guests') return stay.guests;
  const every = lower.match(EVERY_TERM);
  if (every) return parseNumber(every[1]) * Math.ceil(stay.days / Number(every[2]));
  return parseNumber(lower);
};

// A term that cannot be worked out, such as (1every0days), leaves the formula as written.
const isUsableTerm = (term) => {
  const every = term.match(EVERY_TERM);
  return !every || Number(every[2]) > 0;
};

const dependsOnStay = (term) => /^(days|guests)$/i.test(term) || EVERY_TERM.test(term);

// Returns { text, hasFormula, assumed }. assumed is true when a formula used the assumed stay.
export const resolveTaskTitle = (title, nextStay) => {
  let hasFormula = false;
  let assumed = false;
  const stay = nextStay || ASSUMED_STAY;
  const text = String(title || '').replace(FORMULA, (match, before, formula) => {
    const terms = formula.split('*').map((term) => term.trim());
    // Plain numbers such as /3*2 say nothing about a stay, so they stay as written.
    if (!terms.some(dependsOnStay) || !terms.every(isUsableTerm)) return match;
    hasFormula = true;
    if (!nextStay) assumed = true;
    const value = terms.reduce((product, term) => product * termValue(term, stay), 1);
    // Rounded first so that a calculation error like 11.000000000000002 does not become 12.
    return `${before}${Math.ceil(Math.round(value * 1e6) / 1e6)}`;
  });
  return { text, hasFormula, assumed };
};

export const NO_NEXT_STAY_TEXT = `No stay in the next 7 days, so this is calculated with ${ASSUMED_STAY.guests} guests and ${ASSUMED_STAY.days} days.`;
